import React, { useEffect, useState } from 'react';
import { 
  Row, Col, Card, Statistic, Progress, Button, Tag, Typography, Space, Alert,
  Descriptions, Divider, Spin
} from 'antd';
import {
  DollarOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';

const { Title, Paragraph, Text } = Typography;

function Billing() {
  const [usage, setUsage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      const [usageRes, subRes] = await Promise.all([
        api.get('/billing/usage'),
        api.get('/billing/subscription').catch(() => ({ data: null }))
      ]);
      setUsage(usageRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await api.post('/billing/portal');
      console.log('Portal response:', response.data);
      if (!response.data.url) {
        throw new Error('No URL returned from billing portal API');
      }
      const portalUrl = response.data.url;
      console.log('Opening portal URL:', portalUrl);
      
      // Open in new window with specific features to bypass iframe restrictions
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
      console.log('✅ Billing portal opened successfully');
    } catch (error) {
      console.error('Portal error:', error);
      alert('Failed to open billing portal: ' + error.message);
    }
  };

  const getTokenPercent = () => {
    if (!usage || !usage.monthly_token_limit || usage.monthly_token_limit === 0) return 0;
    const percent = Math.round((usage.tokens_used_this_period / usage.monthly_token_limit) * 100);
    return isNaN(percent) ? 0 : Math.min(percent, 100);
  };

  const getStoragePercent = () => {
    if (!usage || !usage.storage_limit_gb || usage.storage_limit_gb === 0) return 0;
    const percent = Math.round((usage.storage_used_gb / usage.storage_limit_gb) * 100);
    return isNaN(percent) ? 0 : Math.min(percent, 100);
  };

  const getDocumentPercent = () => {
    if (!usage || !usage.document_limit || usage.document_limit === 0) return 0;
    const percent = Math.round((usage.document_count / usage.document_limit) * 100);
    return isNaN(percent) ? 0 : Math.min(percent, 100);
  };

  const getUsageColor = (percent) => {
    if (percent >= 90) return '#DC2626';
    if (percent >= 80) return '#D97706';
    return '#0B3D91';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" tip="Loading billing information..." />
      </div>
    );
  }

  const tokenPercent = getTokenPercent();
  const storagePercent = getStoragePercent();
  const documentPercent = getDocumentPercent();

  const usageBreakdownData = usage ? [
    { name: 'Used', value: usage.tokens_used_this_period, color: '#0B3D91' },
    { name: 'Remaining', value: Math.max(0, usage.monthly_token_limit - usage.tokens_used_this_period), color: '#E5E7EB' },
  ] : [];

  const resourceUsageData = usage ? [
    { 
      resource: 'Tokens', 
      used: usage.tokens_used_this_period, 
      limit: usage.monthly_token_limit,
      percent: tokenPercent
    },
    { 
      resource: 'Storage', 
      used: usage.storage_used_gb, 
      limit: usage.storage_limit_gb,
      percent: storagePercent
    },
    { 
      resource: 'Documents', 
      used: usage.document_count, 
      limit: usage.document_limit || 999,
      percent: documentPercent
    },
  ] : [];

  const GST_RATE = 0.10; // Australian GST: 10%
  
  const calculateGST = (price) => {
    return {
      basePrice: price,
      gst: price * GST_RATE,
      total: price * (1 + GST_RATE)
    };
  };

  const PLAN_TIERS = [
    {
      name: 'Individual',
      priceId: 'price_1SWrfHGjGSm6BH3P1Ouc6aIa',
      price: 39, // AUD excluding GST
      tokens: 100000,
      storage: 5,
      documents: 50,
      features: ['100,000 tokens/month', '~50-100 questions', '5GB storage', 'Up to 50 documents', 'Email support']
    },
    {
      name: 'Professional',
      priceId: 'price_1SWrmoGjGSm6BH3Pyf9rvtEr',
      price: 129, // AUD excluding GST
      tokens: 500000,
      storage: 50,
      documents: 500,
      features: ['500,000 tokens/month', '~250-500 questions', '50GB storage', 'Up to 500 documents', 'Priority support'],
      popular: true
    },
    {
      name: 'Enterprise',
      priceId: 'price_1SWrnmGjGSm6BH3PuX9k3GjF',
      price: 399, // AUD excluding GST
      tokens: 2000000,
      storage: 200,
      documents: null,
      features: ['2,000,000 tokens/month', '~1,000-2,000 questions', '200GB storage', 'Unlimited documents', 'Dedicated support', 'Custom integrations']
    },
  ];

  const handleSubscribe = async (priceId) => {
    try {
      const currentUrl = window.location.origin;
      const response = await api.post('/billing/checkout', { 
        price_id: priceId,
        success_url: `${currentUrl}/billing?success=true`,
        cancel_url: `${currentUrl}/billing?canceled=true`
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>
          Billing & Subscription
        </Title>
        <Paragraph type="secondary" style={{ fontSize: '16px', marginBottom: 0 }}>
          Manage your subscription and monitor resource usage
        </Paragraph>
      </div>

      {tokenPercent >= 80 && (
        <Alert
          message="Usage Alert"
          description={`You are using ${tokenPercent}% of your monthly token quota. ${
            tokenPercent >= 100 
              ? 'Your account may experience service limitations. Please upgrade your plan.' 
              : tokenPercent >= 90
              ? 'Consider upgrading your plan to avoid hitting your limit.'
              : 'Monitor your usage to stay within limits.'
          }`}
          type={tokenPercent >= 100 ? 'error' : 'warning'}
          icon={<ExclamationCircleOutlined />}
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      {subscription && subscription.status !== 'none' ? (
        <Card bordered={false} style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 24]} align="middle">
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small">
                <Text type="secondary">Current Plan</Text>
                <Title level={3} style={{ margin: 0 }}>
                  <CrownOutlined style={{ color: '#0B3D91', marginRight: '8px' }} />
                  {subscription.plan_type.charAt(0).toUpperCase() + subscription.plan_type.slice(1)}
                </Title>
                <Tag 
                  color={
                    subscription.status === 'active' ? 'success' :
                    subscription.status === 'trialing' ? 'processing' :
                    subscription.status === 'past_due' ? 'warning' : 'error'
                  }
                  icon={subscription.status === 'active' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                >
                  {subscription.status.toUpperCase()}
                </Tag>
              </Space>
            </Col>
            <Col xs={24} md={12} style={{ textAlign: 'right' }}>
              <Button 
                type="primary" 
                size="large"
                onClick={handleManageSubscription}
                icon={<DollarOutlined />}
              >
                Manage Subscription
              </Button>
            </Col>
          </Row>
        </Card>
      ) : (
        <Alert
          message="No Active Subscription"
          description="Subscribe to a plan to unlock full features and start using IndustrialIQ"
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      {usage && (
        <>
          <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false}>
                <Statistic
                  title="Token Usage"
                  value={tokenPercent}
                  suffix="%"
                  prefix={<ThunderboltOutlined style={{ color: getUsageColor(tokenPercent) }} />}
                  valueStyle={{ color: getUsageColor(tokenPercent), fontSize: '28px', fontWeight: 600 }}
                />
                <Progress 
                  percent={tokenPercent} 
                  strokeColor={getUsageColor(tokenPercent)}
                  showInfo={false}
                  size="small"
                  style={{ marginTop: '8px', marginBottom: '8px' }}
                />
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {usage.tokens_used_this_period.toLocaleString()} / {usage.monthly_token_limit.toLocaleString()}
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false}>
                <Statistic
                  title="Storage Used"
                  value={usage.storage_used_gb.toFixed(2)}
                  suffix={`GB / ${usage.storage_limit_gb}GB`}
                  prefix={<DatabaseOutlined style={{ color: getUsageColor(storagePercent) }} />}
                  valueStyle={{ color: getUsageColor(storagePercent), fontSize: '28px', fontWeight: 600 }}
                />
                <Progress 
                  percent={storagePercent} 
                  strokeColor={getUsageColor(storagePercent)}
                  showInfo={false}
                  size="small"
                  style={{ marginTop: '8px' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false}>
                <Statistic
                  title="Documents"
                  value={usage.document_count}
                  suffix={usage.document_limit ? `/ ${usage.document_limit}` : ''}
                  prefix={<FileTextOutlined style={{ color: '#0B3D91' }} />}
                  valueStyle={{ color: '#111827', fontSize: '28px', fontWeight: 600 }}
                />
                {usage.document_limit && (
                  <Progress 
                    percent={documentPercent} 
                    strokeColor={getUsageColor(documentPercent)}
                    showInfo={false}
                    size="small"
                    style={{ marginTop: '8px' }}
                  />
                )}
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false}>
                <Statistic
                  title="Questions Asked"
                  value={usage.queries_this_period}
                  prefix={<QuestionCircleOutlined style={{ color: '#0284C7' }} />}
                  valueStyle={{ color: '#111827', fontSize: '28px', fontWeight: 600 }}
                />
                <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginTop: '16px' }}>
                  This billing period
                </Text>
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={12}>
              <Card title="Token Usage Distribution" bordered={false}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={usageBreakdownData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {usageBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => value.toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title="Resource Usage Overview" bordered={false}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={resourceUsageData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="resource" stroke="#6B7280" style={{ fontSize: '13px' }} />
                    <YAxis stroke="#6B7280" style={{ fontSize: '13px' }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="percent" fill="#0B3D91" name="Usage %" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Card title="Usage Details" bordered={false} style={{ marginBottom: '24px' }}>
            <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }}>
              <Descriptions.Item label="Token Limit (Monthly)">{usage.monthly_token_limit.toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Tokens Used">{usage.tokens_used_this_period.toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Tokens Remaining">
                <Text type={tokenPercent > 80 ? 'danger' : 'success'} strong>
                  {(usage.monthly_token_limit - usage.tokens_used_this_period).toLocaleString()}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Storage Limit">{usage.storage_limit_gb} GB</Descriptions.Item>
              <Descriptions.Item label="Storage Used">{usage.storage_used_gb.toFixed(2)} GB</Descriptions.Item>
              <Descriptions.Item label="Storage Available">
                <Text type={storagePercent > 80 ? 'danger' : 'success'} strong>
                  {(usage.storage_limit_gb - usage.storage_used_gb).toFixed(2)} GB
                </Text>
              </Descriptions.Item>
              {usage.document_limit && (
                <>
                  <Descriptions.Item label="Document Limit">{usage.document_limit}</Descriptions.Item>
                  <Descriptions.Item label="Documents Uploaded">{usage.document_count}</Descriptions.Item>
                  <Descriptions.Item label="Documents Available">
                    <Text type={documentPercent > 80 ? 'danger' : 'success'} strong>
                      {usage.document_limit - usage.document_count}
                    </Text>
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </Card>
        </>
      )}

      {(!subscription || subscription.status === 'none') && (
        <>
          <Divider><Text strong>Available Plans</Text></Divider>
          <Row gutter={[24, 24]}>
            {PLAN_TIERS.map((plan, index) => (
              <Col xs={24} md={8} key={index}>
                <Card
                  bordered={false}
                  style={{
                    borderRadius: '12px',
                    boxShadow: plan.popular ? '0 8px 24px rgba(11, 61, 145, 0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
                    border: plan.popular ? '2px solid #0B3D91' : 'none',
                    position: 'relative'
                  }}
                >
                  {plan.popular && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: '#0B3D91',
                      color: 'white',
                      padding: '4px 16px',
                      borderRadius: '16px',
                      fontSize: '13px',
                      fontWeight: 600
                    }}>
                      Most Popular
                    </div>
                  )}
                  <Space direction="vertical" size="middle" style={{ width: '100%', textAlign: 'center' }}>
                    <div>
                      <Title level={4} style={{ marginBottom: '4px' }}>{plan.name}</Title>
                      <Title level={2} style={{ margin: 0, color: '#0B3D91' }}>
                        ${plan.price}
                        <Text type="secondary" style={{ fontSize: '16px', fontWeight: 400 }}> AUD</Text>
                      </Title>
                      <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        + ${calculateGST(plan.price).gst.toFixed(2)} GST
                      </Text>
                      <Text strong style={{ fontSize: '15px', display: 'block', marginTop: '4px' }}>
                        ${calculateGST(plan.price).total.toFixed(2)} AUD/month total
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                        (inc GST)
                      </Text>
                    </div>
                    <Divider style={{ margin: '8px 0' }} />
                    <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'left' }}>
                      {plan.features.map((feature, idx) => (
                        <Text key={idx} style={{ fontSize: '14px' }}>
                          <CheckCircleOutlined style={{ color: '#16A34A', marginRight: '8px' }} />
                          {feature}
                        </Text>
                      ))}
                    </Space>
                    <Button 
                      type={plan.popular ? 'primary' : 'default'}
                      size="large" 
                      block
                      style={{ marginTop: '16px' }}
                      onClick={() => handleSubscribe(plan.priceId)}
                    >
                      Subscribe to {plan.name}
                    </Button>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>

          <Card bordered={false} style={{ marginTop: '24px' }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Overage Pricing:</Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                • Additional token usage is billed at actual cost + 10% markup (plus GST)
              </Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                • Upgrade your plan anytime to increase limits
              </Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                • All plans include self-service management and secure user accounts
              </Text>
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary" style={{ fontSize: '13px', fontStyle: 'italic' }}>
                All prices shown in Australian Dollars (AUD) excluding GST. A 10% Goods and Services Tax (GST) is added at checkout. 
                Invoices will display GST amount separately as required by Australian tax law.
              </Text>
              <Divider style={{ margin: '12px 0' }} />
              <Text strong>Need Help?</Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                For subscription inquiries or support, contact us at{' '}
                <a href="mailto:michael@process-iq.com.au" style={{ color: '#0B3D91' }}>michael@process-iq.com.au</a>
              </Text>
            </Space>
          </Card>
        </>
      )}
    </div>
  );
}

export default Billing;
